import { Box, Button, FormControl, IconButton, InputLabel, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { MRTTable } from '../../components/MRTTable';
import { Add, Edit } from '@mui/icons-material';
import { EmployeesAPI } from '../../fetch/employee';
import { useToastContext } from '../../hooks';
import { useParams } from 'react-router-dom';
import { IAgentDrawsAndDeductionsAPI } from '../../fetch/agent-draws-and-deductions';
import { GroupedStatuses, IAgentDraw, Status } from '../../models/agent-draws-and-deductions/agent-draw';
import { UserContext } from '../../context/user';
import { IRoleType } from '../../models/roles';
import AddEditAgentDrawModal from './include/modals/AddEditAgentDrawModal';

const buildConfig = require('../../buildSettings.json');

const AuditTrailCell: React.FC<{ updatedDateString: string; createdDateString: string; updatedBy: string; className?: string;  }> = ({
  updatedDateString = '',
  createdDateString = '',
  updatedBy = '',
  className = ''
}) => {
  const Bold = (chunks: React.ReactNode) => (
    <Typography component="span" fontWeight="500" variant="caption">
      {chunks}
    </Typography>
  );

  if (updatedDateString) {
    return (
      <Typography variant="caption" color="text.secondary" className={className}>
        {updatedBy ? <>Updated by {Bold(updatedBy)} on {Bold(updatedDateString)}</> : <>Updated on {Bold(updatedDateString)}</>}
      </Typography>
    );
  }

  if (createdDateString) {
    return (
      <Typography variant="caption" color="text.secondary" className={className}>
        {updatedBy ? <>Added by {Bold(updatedBy)} on {Bold(createdDateString)}</> : <>Added on {Bold(createdDateString)}</>}
      </Typography>
    );
  }

  // Fallback
  return (
    <Typography
      variant="body2"
      color="text.disabled"
      fontStyle="italic"
      className={className}
    >
      —
    </Typography>
  );
};

interface TruncatedCellProps {
  text: string;
  className?: string;
}

const TruncatedCell: React.FC<TruncatedCellProps> = ({ text }) => {
  return (
    <Typography
      variant="caption"
      component="div"
      sx={{
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'unset',
        wordBreak: 'break-word',
      }}
    >
      {text}
    </Typography>
  );
};

// Negative draws applied to a target draw carry this status text prefix and are read only.
const APPLIED_CREDIT_PREFIX = 'Applied as credit';
const isAppliedNegative = (draw: IAgentDraw) =>
  Number(draw.amount) < 0 && String(draw.status ?? '').trimStart().startsWith(APPLIED_CREDIT_PREFIX);

// The API exception handler serializes ProblemDetails with PascalCase keys while other endpoints
// use camelCase; read both so validation messages reach the user instead of a generic toast.
const errorDetail = (err: any): string | undefined => err?.detail || err?.Detail;

let cachedStatuses: GroupedStatuses<Status> | null = null;
const AgentDrawsDetails = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const { showToast } = useToastContext();
  const showToastRef = React.useRef(showToast);
  useEffect(() => { showToastRef.current = showToast; }, [showToast]);
  const notify = useCallback((...args: Parameters<typeof showToast>) => {
    showToastRef.current?.(...args);
  }, []);
  const [page, setPage] = useState(0);
  const { userContext } = React.useContext(UserContext);
  const [perPage, setRowsPerPage] = useState(25);
  const [recordCount, setRecordCount] = useState(0);
  const [loadingDraws, setLoadingDraws] = useState(false);
  const [employee, setEmployee] = useState({employeeId: '', firstName: '', lastName: '', loading: false});
  // const [selectedDrawId, setSelectedDrawId] = useState<number | null>(null);
  const [rows, setRows] = useState<IAgentDraw[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [editRow, setEditRow] = useState<IAgentDraw | null>(null);
  const [statuses, setStatuses] = useState<GroupedStatuses<Status> | null>(cachedStatuses);
  const [globalFilter, setGlobalFilter] = useState<string>('');
  
  // Filter Approval states
  const [approvalStatusFilter, setApprovalStatusFilter] = useState<string>('');
  const [drawFinancialStatusFilter, setDrawFinancialStatusFilter] = useState<number | ''>('');
  
  // Ledger totals
  const [creditTotals, setCreditTotals] = useState<number>(0);
  const [balanceTotals, setBalanceTotals] = useState<number>(0);
  const [amountTotals, setAmountTotals] = useState<number>(0);
  
  const [refreshKey, setRefreshKey] = useState(0);
  
  const canSendDrawEmails = userContext?.featureFlags?.some(
    (flag: any) => flag?.name === 'email-draw-to-agent' && flag?.hasAccess
  ) ?? false;

  const canSendApprovalEmails = userContext?.featureFlags?.some(
    (flag: any) => flag?.name === 'email-approval-to-agent' && flag?.hasAccess
  ) ?? false;

  // Notify Line Lead toggle is only relevant when draw emails are enabled
  const canToggleNotifyLineLead = canSendDrawEmails;

  // Require Agent Approval toggle is only relevant when you want to send emails to the agent asking for approval
  const canToggleRequireAgentApproval = canSendApprovalEmails;

  //fetch employee details using the employeeId
  const fetchEmployee = useCallback(async () => {
        setEmployee(prev => ({...prev, loading: true}));
        try {
          const response = await EmployeesAPI.fetchEmployee(employeeId);

          if (response) {
            setEmployee(prev => ({...prev, employeeId: '22', firstName: response.firstName, lastName: response.lastName}));
          } else {
            setEmployee(prev => ({...prev, firstName: '', lastName: ''}));
          }
        } catch (error: any) {
          notify('error', errorDetail(error) || 'Error trying to retrieve Employee details. Please try again.');
        } finally {
          setEmployee(prev => ({...prev, loading: false}));
        }
  }, [employeeId, notify]);
  
  useEffect(() => {
    if (cachedStatuses) {
      setStatuses(cachedStatuses);
      // setLoading(false);
      return;
    }

    const fetchStatuses = async () => {
      try {
        const res = await IAgentDrawsAndDeductionsAPI.fetchStatuses();
        // if (!res.ok) throw new Error('Failed to fetch statuses');
        const data: GroupedStatuses<Status> = res;
        cachedStatuses = data;
        setStatuses(data);
      } catch (err) {
        // setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        // setLoading(false);
      }
    };

    fetchStatuses();
  }, []);

  useEffect(() => {
  const fetchDraws = async () => {
    setLoadingDraws(true);
    try {
      const response = await IAgentDrawsAndDeductionsAPI.getDrawsByAgentId(Number(employeeId), {
        page: page + 1,
        perPage,
        search: globalFilter,
        approvalStatusFilter: approvalStatusFilter || undefined,
        drawFinancialStatusFilter: drawFinancialStatusFilter !== '' ? drawFinancialStatusFilter : null,
      });
      setRows(response.records);
      setRecordCount(response.totalRecordCount);
      setAmountTotals(response.amountTotals);
      setCreditTotals(response.creditTotals);
      setBalanceTotals(response.balanceTotals);
    } catch (error: any) {
      notify('error', errorDetail(error) || 'Error trying to retrieve Draws. Please try again.');
    } finally {
      setLoadingDraws(false);
    }
  };
    fetchDraws();
  }, [page, perPage, employeeId, notify, globalFilter, approvalStatusFilter, drawFinancialStatusFilter, refreshKey]);
  
  useEffect(() => {
    if (!employeeId) {
      notify('error', 'Employee ID is missing');
      return;
    } else {
      fetchEmployee();
    }
  }, [fetchEmployee, employeeId, notify]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  
  const getStatusName = (id: number, type: string): string => {
    if (!statuses) return '';
    const keys = Object.keys(statuses);
    const matchKey = keys.find(k => k.toLowerCase() === type.toLowerCase());
    if (!matchKey) return `Status ${id}`;
    const arr: any[] = (statuses as any)[matchKey] || [];
    return arr.find(x => x.value === id)?.description || `Status ${id}`;
  }

  // Format currency for display
  const formatCurrency = (value: number): string => {
    if (isNaN(value)) return '$—';
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);
    return `${sign}$${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
    
  const hasAddAccess = !!userContext?.roles?.has(IRoleType.Accounting) || !!userContext?.roles?.has(IRoleType.SystemAdmin);
  const handleOpenAdd = () => { if(hasAddAccess) { setEditRow(null); setIsModalOpen(true); } };
  const handleCloseModal = () => { if(!saving && !savingEdit) { setIsModalOpen(false); setEditRow(null); } };
  const handleCreate = async (payload: { date: string; amount: number; memo?: string; notifyLineLead?: boolean; requireAgentApproval?: boolean; targetDrawId?: number | null }) => {
    if(saving) return;
    setSaving(true);
    try {
      await IAgentDrawsAndDeductionsAPI.createDraw(Number(employeeId), {
        ...payload,
        notifyLineLead: payload.notifyLineLead ?? true,
        requireAgentApproval: payload.requireAgentApproval ?? true,
      });
      notify('success', 'Draw created');
      handleCloseModal();
      setRefreshKey(prev => prev + 1); 
    } catch(err: any){
      notify('error', errorDetail(err) || 'Failed to create draw');
    } finally { setSaving(false); }
  };

  const handleOpenEdit = (row: IAgentDraw) => {
    if (!hasAddAccess) return;
    setEditRow(row);
    setIsModalOpen(true);
  };


  const handleEditSave = async (drawId: number, payload: { date: string; amount: number; memo?: string; drawFinancialStatusId?: number | ''; notifyLineLead?: boolean; requireAgentApproval?: boolean; targetDrawId?: number | null }) => {
    if (savingEdit) return;
    setSavingEdit(true);
    try {
      // Update date/amount/memo
      await IAgentDrawsAndDeductionsAPI.updateDraw(drawId, { date: payload.date, amount: payload.amount, memo: payload.memo, notifyLineLead: payload.notifyLineLead, requireAgentApproval: payload.requireAgentApproval, targetDrawId: payload.targetDrawId });

      // If status provided and differs from current row, update. Skipped for negative amounts:
      // the negative-draw workflow owns the status (Forgiven / applied as credit) server side.
      const current = rows.find(r => r.id === drawId);
      if (payload.amount >= 0 && payload.drawFinancialStatusId !== undefined && payload.drawFinancialStatusId !== '' && current?.drawFinancialStatusId !== Number(payload.drawFinancialStatusId)) {
        await IAgentDrawsAndDeductionsAPI.updateDrawFinancialStatus(drawId, Number(payload.drawFinancialStatusId));
      }

      notify('success', 'Draw updated');
      handleCloseModal();
      setRefreshKey(prev => prev + 1);
    } catch (err: any) {
      notify('error', errorDetail(err) || 'Failed to update draw');
    } finally { setSavingEdit(false); }
  };

  const columns = useMemo(() => {
    return [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        enableSorting: false, 
        enableGlobalFilter: true, 
        footer: 'Totals',
      },
      {
        accessorKey: 'drawDateString',
        header: 'Date',
        size: 90,
        enableSorting: false, 
        enableGlobalFilter: false,
      },
      {
        accessorKey: 'amount',
        header: 'Draw Amount',
        size: 120,
        enableSorting: false, 
        enableGlobalFilter: false,
        Cell: ({ cell }: any) => formatCurrency(Number(cell.getValue())),
        footer: formatCurrency(amountTotals),
      },
      {
        accessorKey: 'credit',
        header: 'Credit',
        size: 110,
        enableSorting: false, 
        enableGlobalFilter: false,
        Cell: ({ cell }: any) => formatCurrency(Number(cell.getValue())),
        footer: formatCurrency(creditTotals),
      },
      {
        accessorKey: 'balance',
        header: 'Balance',
        size: 110,
        enableSorting: false, 
        enableGlobalFilter: false,
        Cell: ({ cell }: any) => formatCurrency(Number(cell.getValue())),
        footer: formatCurrency(balanceTotals),
      },
      {
        accessorKey: 'transactions',
        header: 'Transaction ID',
        size: 200,
        enableSorting: false, 
        enableGlobalFilter: false,
        muiTableHeadCellProps: { sx: { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset', maxWidth: 'none', backgroundColor: (theme: any) => (theme?.frankie?.searchBlue ?? '#0B3C5D'), color: '#eff4f6', verticalAlign: 'middle' } },
        muiTableBodyCellProps: { sx: { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset', maxWidth: 'none' } },
        Cell: ({ row }: any) => {
          const transactions = row.original.transactions as { invoiceNumber: string; commissionBookingId: number }[] | undefined;
          if (!transactions || transactions.length === 0) return <span />;
          return (
            <Typography variant="caption" component="div">
              {transactions.map((tx, idx) => {
                const cbsUrl = `${buildConfig.FRANKLIN_ST_V2_URL}/v2/ActivityDocuments/CommissionBookingStatement/${tx.commissionBookingId}`;
                return (
                  <React.Fragment key={`${tx.invoiceNumber}-${tx.commissionBookingId}`}>
                    <a 
                      href={cbsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1976d2', textDecoration: 'underline' }}
                    >
                      {tx.invoiceNumber}
                    </a>
                    {idx < transactions.length - 1 && ', '}
                  </React.Fragment>
                );
              })}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'memo',
        header: 'Memo',
        size: 180,
        enableSorting: false, 
        enableGlobalFilter: true, 
        muiTableHeadCellProps: { sx: { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset', maxWidth: 'none', backgroundColor: (theme: any) => (theme?.frankie?.searchBlue ?? '#0B3C5D'), color: '#eff4f6', verticalAlign: 'middle' } },
        muiTableBodyCellProps: { sx: { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset', maxWidth: 'none' } },
        Cell: ({ cell }: any) => <TruncatedCell text={String(cell.getValue() ?? '')} />,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 100,
        enableSorting: false, 
        enableGlobalFilter: false,
        Cell: ({ row }: any) => {
          const original = row.original;
          const hasSent = original.emailSentDate !== null && original.emailSentDate !== undefined;
          const statusLower = (original.status ?? '').toLowerCase();
          const hasResponded = statusLower.startsWith('accepted') || statusLower.startsWith('declined');
          // Negative draws are credits that need no agent approval; never email for them.
          const isNegative = Number(original.amount) < 0;
          return (
            <Button
              size="small"
              onClick={async () => {
                if (sendingId || !canSendDrawEmails || hasResponded || isNegative) return;
                try {
                  setSendingId(original.id);
                  await IAgentDrawsAndDeductionsAPI.sendApprovalEmail(original.id);
                  setRefreshKey(prev => prev + 1);
                  notify('success', 'Approval email sent');
                } catch (err: any) {
                  notify('error', errorDetail(err) || 'Failed to send email');
                } finally {
                  setSendingId(null);
                }
              }}
              variant="contained"
              sx={{
                minWidth: 90,
                height: 30,
                fontSize: '0.72rem',
                whiteSpace: 'nowrap',
                px: 0.5,
                '@media (max-width: 1290px)': { width: '100%' },
              }}
              disabled={!canSendDrawEmails || !!sendingId || hasResponded || isNegative}
            >
              {sendingId === original.id ? 'Sending...' : (hasSent ? 'Resend' : 'Send')}
            </Button>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Approval Status',
        size: 130,
        enableSorting: false,
        enableColumnActions: false,
        enableHiding: false,
        enableGlobalFilter: false,
        Cell: ({ row }: any) => {
          const status = row.original.status;
          // quick fix: Filter out empty, "0", or "1" values from old int-based records
          if (!status || status === '' || status === '0' || status === '1') return <span />;
          return (
            <Tooltip title={status} placement="top" arrow>
              <Typography
                variant="caption"
                component="span"
                sx={{ cursor: 'default', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {status}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'drawFinancialStatusId',
        header: 'Draw Financial Status',
        size: 150,
        enableSorting: false,
        enableColumnActions: false,
        enableGlobalFilter: false,
        muiTableHeadCellProps: { sx: { minWidth: 150, backgroundColor: (theme: any) => (theme?.frankie?.searchBlue ?? '#0B3C5D'), color: '#eff4f6', verticalAlign: 'middle' } },
        muiTableBodyCellProps: { sx: { minWidth: 150 } },
        Cell: ({ row }: any) => {
          const id = row.original.drawFinancialStatusId;
          if (!id || id === 0) return <span />;
          return <span>{getStatusName(id, 'drawFinancialStatus')}</span>;
        },
      },
      {
        accessorKey: 'history',
        header: 'History',
        size: 280,
        enableSorting: false,
        enableGlobalFilter: false,
        muiTableHeadCellProps: { sx: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 'none', backgroundColor: (theme: any) => (theme?.frankie?.searchBlue ?? '#0B3C5D'), color: '#eff4f6', verticalAlign: 'middle' } },
        muiTableBodyCellProps: { sx: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 'none' } },
        Cell: ({ row }: any) => (
          <AuditTrailCell
            updatedDateString={row.original.updatedDateString}
            createdDateString={row.original.createdDateString}
            updatedBy={row.original.updatedBy}
          />
        ),
      },
      {
        accessorKey: 'actions',
        header: hasAddAccess ? (
          <Button
            size="small"
            onClick={handleOpenAdd}
            variant="contained"
            sx={{
              minWidth: 90,
              height: 30,
              fontSize: '0.72rem',
              whiteSpace: 'nowrap',
              px: 0.5,
              '@media (max-width: 1290px)': { width: '100%' },
            }}
            startIcon={<Add />}
          >
            Add
          </Button>
        ) : '',
        size: 80,
        enableSorting: false,
        enableColumnActions: false,
        enableHiding: false,
        enableGlobalFilter: false,
        Cell: ({ row }: any) => {
          if (!hasAddAccess) return <span />;
          // An applied negative is read only: its credit already lives on the target draw.
          if (isAppliedNegative(row.original)) {
            return (
              <Tooltip title="This negative draw has been applied to another draw and can no longer be edited" placement="top" arrow>
                <span>
                  <IconButton disabled>
                    <Edit />
                  </IconButton>
                </span>
              </Tooltip>
            );
          }
          return (
            <IconButton onClick={() => handleOpenEdit(row.original)}>
              <Edit />
            </IconButton>
          );
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statuses, hasAddAccess, canSendDrawEmails, sendingId, amountTotals, creditTotals, balanceTotals]);

  // Approval status filter options - simple Accepted/Declined to match Status column values

  const approvalStatusOptions = [
    { value: 'accepted', label: 'Accepted' },
    { value: 'declined', label: 'Declined' },
    { value: 'pending approval', label: 'Pending Approval' },
    { value: 'blank', label: 'Blank' },
  ];
  
  // Get draw financial status options for filter dropdown
  const drawFinancialStatusOptions = useMemo(() => {
    if (!statuses) return [];
    const drawFinancialStatuses = (statuses as any).drawFinancialStatus || [];
    return drawFinancialStatuses.map((s: any) => ({
      value: s.value,
      label: s.description || s.text,
    }));
  }, [statuses]);

  return (
      <div>
      {/* Header with title and filters */}
      <Box position="sticky" top={70} bgcolor="background.paper" zIndex={1} pb={2}>
        <Box display="flex" alignItems="center" gap={3} flexWrap="nowrap">
          <Typography variant={'h6'} sx={{ minWidth: 280, whiteSpace: 'nowrap' }}>
            Draws for {employee.firstName.length > 0 ? `${employee.firstName} ${employee.lastName}` : 'Unknown'}
          </Typography>
          
          {/* Filter dropdowns - next to the title */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="approval-status-filter-label">Approval Status</InputLabel>
            <Select
              labelId="approval-status-filter-label"
              id="approval-status-filter"
              value={approvalStatusFilter}
              label="Approval Status"
              onChange={(e) => {
                setApprovalStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {approvalStatusOptions.map((opt: any) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="draw-financial-status-filter-label">Draw Financial Status</InputLabel>
            <Select
              labelId="draw-financial-status-filter-label"
              id="draw-financial-status-filter"
              value={drawFinancialStatusFilter}
              label="Draw Financial Status"
              onChange={(e) => {
                setDrawFinancialStatusFilter(e.target.value as number | '');
                setPage(0);
              }}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {drawFinancialStatusOptions.map((opt: any) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box>
        <Box
          sx={{
            
            '& thead th:first-of-type': {
              minWidth: 80,
              width: 80,
              paddingLeft: '30px',
              textAlign: 'left',
            },
            '& tbody td:first-of-type': {
              minWidth: 80,
              width: 80,
              paddingLeft: '30px',
              textAlign: 'left',
            },
            '& tfoot td:first-of-type': {
              minWidth: 80,
              width: 80,
              paddingLeft: '30px',
              textAlign: 'left',
            },
            // Override shared second column (Date) width rules
            '& thead th:nth-of-type(2), & tbody td:nth-of-type(2), & tfoot td:nth-of-type(2)': {
              minWidth: '90px !important',
              width: '90px !important',
              maxWidth: '100px !important',
            },
            
            '& tbody td': {
              borderBottom: '1px solid rgb(212, 210, 210)',
            },
          }}
        >
          <MRTTable
            columns={columns as any}
            data={rows}
            loading={loadingDraws}
            manualPagination
            totalRowCount={recordCount}
            pageIndex={page}
            pageSize={perPage}
            onPaginationChange={(updater: any) => {
              const next = typeof updater === 'function' ? updater({ pageIndex: page, pageSize: perPage }) : updater;
              if (next?.pageIndex !== undefined) setPage(next.pageIndex);
              if (next?.pageSize !== undefined) setRowsPerPage(next.pageSize);
            }}
            globalFilter={globalFilter}
            onGlobalFilterChange={(val: any) => {
              setGlobalFilter(val ?? '');
              setPage(0);
            }}
          />
        </Box>
        <AddEditAgentDrawModal
          open={isModalOpen}
          onClose={handleCloseModal}
          initialData={editRow}
          statuses={statuses}
          employeeId={Number(employeeId)}
          onCreate={handleCreate}
          onEditSave={handleEditSave}
          saving={editRow ? savingEdit : saving}
          showNotifyLineLead={canToggleNotifyLineLead}
          showRequireAgentApproval={canToggleRequireAgentApproval}
        />
        </Box>
      </div>
  )
}

export default AgentDrawsDetails;